import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useMutation } from '@apollo/client';
import { useInterpret, useSelector } from '@xstate/react';
import { useCoreGoalsContext } from 'modules/predictiveGoals/components/contexts/CoreGoalsContext/CoreGoalsContext';
import { createPredictiveAudienceGoalMutation } from 'api/mutations/predictiveAudiences';
import { fetchPreviewUsers } from 'api/queries/predictiveAudiences';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';

import {
  constructGoalLimitResponse,
  constructGoalUniqueNameAndPropertyResponseErrorMsg,
  GOAL_CREATE_ERROR,
} from '../../../../constants';
import { CreatePredictiveGoalResponse, CreatePredictiveGoalVariables } from '../../../../types';
import {
  MutateGoalStates,
  createMutateStateMachineEffects,
  mutateGoalStateMachine,
} from '../../hooks/useMutateGoal.state';
import { reportGoalCreationError } from '../../../../observabilityUtils';

export interface UseCreateGoal {
  setHasCreateLimitError: (setter: boolean) => void;
  setUnsavedChanges: (setter: boolean) => void;
  onPreviewComplete: () => void;
  onPreviewError: () => void;
}
export const useCreateGoal = ({
  setHasCreateLimitError,
  setUnsavedChanges,
  onPreviewComplete,
  onPreviewError,
}: UseCreateGoal) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    goals: { retrigger },
  } = useCoreGoalsContext();
  const { fetch: queryPreviewUsers } = useLazyGraphql(fetchPreviewUsers, {
    fetchPolicy: 'no-cache',
  });
  const [createGoal] = useMutation<CreatePredictiveGoalResponse, CreatePredictiveGoalVariables>(
    createPredictiveAudienceGoalMutation,
  );
  const effects = useMemo(
    () =>
      createMutateStateMachineEffects<CreatePredictiveGoalResponse, CreatePredictiveGoalVariables>({
        fetchGoalPreview: queryPreviewUsers,
        mutateGoal: createGoal,
        onMutationError: (graphqlError) => {
          reportGoalCreationError(graphqlError);
          setUnsavedChanges(true);
          if (graphqlError?.message === constructGoalLimitResponse()) {
            setHasCreateLimitError(true);
          } else if (graphqlError?.message === constructGoalUniqueNameAndPropertyResponseErrorMsg) {
            dispatch(addNegativeToast(`${constructGoalUniqueNameAndPropertyResponseErrorMsg}.`));
          } else {
            dispatch(addNegativeToast(GOAL_CREATE_ERROR));
          }
        },
        onMutateComplete: ({ predictiveAudienceGoalCreate }) => {
          retrigger();
          const goalId = predictiveAudienceGoalCreate.id;
          dispatch(
            addPositiveToast(
              `Successfully created new goal ${predictiveAudienceGoalCreate.name} with id ${goalId}`,
            ),
          );
          history.push(`/predictive-audiences/goals/${goalId}`);
        },
        onPreviewComplete: () => {
          onPreviewComplete();
        },
        onPreviewError,
        shouldConfirmMutation: true,
      }),
    [setHasCreateLimitError, setUnsavedChanges],
  );
  const service = useInterpret(mutateGoalStateMachine, effects);
  const isCreatingGoal = useSelector(service, (state) => {
    return [MutateGoalStates.previewGoal, MutateGoalStates.mutateGoal].some(state.matches);
  });
  const isCreatePreviewInvalid = useSelector(service, (state) => {
    return state.matches(MutateGoalStates.previewInvalid);
  });
  const isCreatePreviewError = useSelector(service, (state) => {
    return state.matches(MutateGoalStates.previewError);
  });
  const usersCount = useSelector(service, (state) => {
    return state.context.totalCount;
  });

  return {
    createGoal: (goalVars: CreatePredictiveGoalVariables) => {
      service.send(MutateGoalStates.previewGoal, {
        payload: goalVars,
      });
    },
    isCreatingGoal,
    isCreatePreviewInvalid,
    isCreatePreviewError,
    service,
    usersCount,
    confirmUpdate: () => service.send(MutateGoalStates.mutateGoal),
  };
};
